// packages/api-client/src/offlineQueue.ts
// 类型定义在**本文件**（R27 把接口冻结在这里；不要另开一个 types 文件 —— 那会让
// 「接口在哪」这个问题有两个答案）。

/** 队列里的一条待上传记录（R27 的 `PendingOrder`，库名 `cuito-tenant`） */
export interface PendingOrder {
  id: string;
  payload: unknown;
  createdAt: string;
}

export interface OfflineQueueStore {
  queueOrder(payload: unknown): Promise<void>;
  getPendingOrders(): Promise<PendingOrder[]>;
  clearPendingOrder(id: string): Promise<void>;
  syncPendingOrders(
    sendFn: (payload: unknown) => Promise<unknown>,
  ): Promise<{ ok: number; failed: number }>;
}

const STORE = 'pending-orders';
const DEFAULT_DB = 'cuito-tenant';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB_REQUEST_FAILED'));
  });
}

function openDb(factory: IDBFactory, name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(name, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB_OPEN_FAILED'));
  });
}

/**
 * 打开（必要时创建）队列库。`idbFactory` 可注入 —— jsdom 没有 `indexedDB`，
 * 单测注入一个内存假实现；生产传 `globalThis.indexedDB`。
 */
export function createOfflineQueue(idbFactory: IDBFactory, dbName: string = DEFAULT_DB): OfflineQueueStore {
  const store = async <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await openDb(idbFactory, dbName);
    try {
      return await request(fn(db.transaction(STORE, mode).objectStore(STORE)));
    } finally {
      db.close();
    }
  };

  return {
    async queueOrder(payload) {
      const row: PendingOrder = {
        id: globalThis.crypto.randomUUID(),
        payload,
        createdAt: new Date().toISOString(),
      };
      await store('readwrite', (s) => s.put(row) as unknown as IDBRequest<void>);
    },
    getPendingOrders: () => store('readonly', (s) => s.getAll() as IDBRequest<PendingOrder[]>),
    async clearPendingOrder(id) {
      await store('readwrite', (s) => s.delete(id) as unknown as IDBRequest<void>);
    },
    async syncPendingOrders(sendFn) {
      const items = await store('readonly', (s) => s.getAll() as IDBRequest<PendingOrder[]>);
      let ok = 0;
      let failed = 0;
      for (const item of items) {
        try {
          await sendFn(item.payload);
          await store('readwrite', (s) => s.delete(item.id) as unknown as IDBRequest<void>);
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      return { ok, failed };
    },
  };
}
